import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do I get more moving company leads in Colorado Springs?",
    a: "Springs has tens of thousands of military households cycling through Fort Carson, Peterson SFB, Schriever SFB, and the Air Force Academy every year. Most movers ignore the PCS angle and lose those searches to national van lines. We build base-specific landing pages, run Google Ads in PCS season (May–August), and tune your Google Business Profile for both Moving Company and Self Storage Service categories. Full guide: /answers/how-to-get-more-moving-leads-colorado-springs.",
  },
  {
    q: "What's the best marketing channel for a Colorado Springs moving company?",
    a: "Local SEO + Google reviews is the highest-ROI channel because moving is a high-trust local search. Google Ads is essential during PCS season (May–August). Most independent Springs movers don't need much social media — base-specific landing pages and review velocity move the needle faster.",
  },
  {
    q: "How competitive is moving company SEO in Colorado Springs?",
    a: "Moderately competitive — much less crowded than Denver. Springs has roughly 30 active moving companies competing for local searches, and most are weak on Google Business Profile and review velocity. A focused independent can move into the Map Pack within 90–120 days.",
  },
  {
    q: "Should I focus on military moves or local civilian moves?",
    a: "Both, but build separate landing pages for each. Military moves (PCS, DITY, Per-Diem) are an underserved high-volume niche. Civilian local moves are higher per-job revenue. The website needs to speak to both audiences without confusing either.",
  },
  {
    q: "Do you handle long-distance and corporate relocation marketing?",
    a: "We focus on local + military for Colorado Springs movers because that's where independents win. Long-distance is dominated by national van lines with massive ad budgets. Corporate relocation is worth pursuing if you have an existing referral relationship with a Springs HR team — we'll build pages around that audience specifically.",
  },
];

export const Route = createFileRoute("/moving-company-marketing-colorado")({
  head: () =>
    industryMarketingHead("Moving Company", "moving company", "moving-company-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Moving Company"
      industryLower="moving company"
      slug="moving-company-marketing-colorado"
      subheadline="We help Colorado Springs moving companies own PCS season — Fort Carson, Peterson, Schriever, and the Air Force Academy."
      painPoints={[
        "National van lines (United, Allied, Mayflower) dominate generic 'movers Colorado Springs' searches",
        "PCS military households are an underserved high-volume audience nobody markets to",
        "Storage upsells get missed because GBP isn't dual-listed",
      ]}
      serviceDescriptions={{
        seo: "Rank for movers, military moves, PCS, DITY help, and storage across Colorado Springs and the Front Range.",
        googleMaps: "Dominate the local Map Pack so 'mover near me' calls come to your truck first.",
        webDesign: "Fast, mobile-first mover sites with base-specific landing pages and instant quote tools.",
        ppc: "Profitable Google Ads timed to PCS season (May–August) and corporate relocation cycles.",
        geoAi: "Get cited when military households ask ChatGPT 'best mover for Fort Carson PCS' or 'DITY moving help'.",
        socialMedia: "Move-day content and military testimonial video that build trust faster than any sales page.",
      }}
      testimonial={{
        quote:
          "PCS season used to overwhelm us in chaos. Now we're booked solid by April with the right kind of jobs — military households who actually pay on time.",
        role: "Moving Company Owner",
        city: "Colorado Springs",
      }}
      faqs={FAQS}
      cityHubLinks={[
        { to: "/colorado-springs", label: "Moving company marketing in Colorado Springs", description: "Help Springs movers own PCS season across Fort Carson, Peterson, Schriever, and the Academy." },
        { to: "/denver-digital-marketing", label: "Moving company marketing in Denver", description: "Compete in metro Denver — local, long-distance, and corporate relocation." },
        { to: "/castle-rock-digital-marketing", label: "Moving company marketing in Castle Rock", description: "Premium residential moves across Castle Rock, Castle Pines, and Parker." },
      ]}
    />
  ),
});
